import { useEffect, useRef, useState } from "react";

export interface UploadPhotosPreview {
  /** A browser URL for the photo, made from the file the shipper picked. */
  url: string;
  /** False while the photo is still uploading. */
  attached: boolean;
}

/**
 * Previews of every photo taken during this visit, oldest first. A photo shows
 * as soon as it's picked, so its upload progress can be drawn over it; a photo
 * that fails to upload is dropped, since it never reached the shipment. Each
 * URL keeps its photo in memory until released, so whatever is left is
 * released when the page goes away.
 */
export function useUploadPhotosPreviews() {
  const [previews, setPreviews] = useState<UploadPhotosPreview[]>([]);
  const previewsRef = useRef<UploadPhotosPreview[]>([]);
  // The photo whose upload finished most recently, so the viewer can play the
  // "attached" tick on it once.
  const [justAttachedUrl, setJustAttachedUrl] = useState<string | undefined>();

  function update(next: UploadPhotosPreview[]) {
    previewsRef.current = next;
    setPreviews(next);
  }

  /** Shows the photo straight away, as uploading, and returns its URL. */
  function addUploading(file: File): string {
    const url = URL.createObjectURL(file);
    update([...previewsRef.current, { url, attached: false }]);
    return url;
  }

  function markAttached(url: string) {
    update(
      previewsRef.current.map((preview) =>
        preview.url === url ? { ...preview, attached: true } : preview,
      ),
    );
    setJustAttachedUrl(url);
  }

  /** Stops the tick replaying when the viewer is shown again after Done. */
  function forgetJustAttached() {
    setJustAttachedUrl(undefined);
  }

  function remove(url: string) {
    URL.revokeObjectURL(url);
    update(previewsRef.current.filter((preview) => preview.url !== url));
  }

  useEffect(() => {
    return () => {
      for (const preview of previewsRef.current) {
        URL.revokeObjectURL(preview.url);
      }
      previewsRef.current = [];
    };
  }, []);

  return {
    previews,
    justAttachedUrl,
    addUploading,
    markAttached,
    forgetJustAttached,
    remove,
  };
}
